import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CancelIcon from '@material-ui/icons/Cancel';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
    gap: theme.spacing(1),
  },
  warningBox: {
    backgroundColor: theme.palette.warning.light,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
}));

const BookingCancelPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [cancelReason, setCancelReason] = useState('');
  const [refundMethod, setRefundMethod] = useState('original_method');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchBooking();
  }, [id]);

  const fetchBooking = async () => {
    setLoadingData(true);
    try {
      const bookingDoc = await getDoc(doc(firestore, 'bookings', id));
      
      if (bookingDoc.exists()) {
        setBooking({
          id: bookingDoc.id,
          ...bookingDoc.data()
        });
      } else {
        // Données de démonstration
        const demoBooking = {
          id: id,
          tripId: '1',
          tripName: 'Paris - Lyon',
          departureDate: '2025-04-15T08:00:00',
          arrivalDate: '2025-04-15T12:30:00',
          passengerName: 'Alice Martin',
          passengerEmail: 'alice.martin@example.com',
          passengerPhone: '+33612345678',
          seats: 2,
          totalPrice: 91.98,
          status: 'Confirmed',
          bookingDate: '2025-03-20T14:30:00',
          paymentMethod: 'Carte bancaire',
          paymentStatus: 'Paid'
        };
        setBooking(demoBooking);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération de la réservation',
        severity: 'error'
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      setSnackbar({
        open: true,
        message: 'Veuillez indiquer une raison d\'annulation',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        status: 'Cancelled',
        cancelReason: cancelReason,
        cancelDate: new Date().toISOString(),
        refundMethod: refundMethod,
        paymentStatus: refundMethod !== 'no_refund' ? 'Refunded' : 'Paid',
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(firestore, 'bookings', id), updateData);

      setSnackbar({
        open: true,
        message: 'Réservation annulée avec succès',
        severity: 'success'
      });

      // Redirect after a short delay
      setTimeout(() => {
        navigate(`/admin/bookings/${id}`);
      }, 1500);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'annulation de la réservation',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/admin/bookings/${id}`);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  if (loadingData) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (!booking) {
    return (
      <Box className={classes.root}>
        <Paper className={classes.paper}>
          <Typography variant="h6">Réservation non trouvée</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/bookings')}
            style={{ marginTop: '16px' }}
          >
            Retour à la liste
          </Button>
        </Paper>
      </Box>
    );
  }

  if (booking.status === 'Cancelled') {
    return (
      <Box className={classes.root}>
        <Paper className={classes.paper}>
          <Typography variant="h6">Cette réservation est déjà annulée</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            style={{ marginTop: '16px' }}
          >
            Retour aux détails
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            style={{ marginRight: '16px' }}
          >
            Retour
          </Button>
          <Typography variant="h4" component="h1">
            Annuler la réservation #{booking.id}
          </Typography>
        </Box>

        <Box className={classes.warningBox}>
          <Typography variant="h6" gutterBottom>
            ⚠️ Attention
          </Typography>
          <Typography variant="body2">
            Vous êtes sur le point d'annuler cette réservation. Cette action mettra à jour le statut de la réservation 
            et libérera les places réservées. Le remboursement sera traité selon la méthode sélectionnée.
          </Typography>
        </Box>

        <Divider />

        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Détails de la réservation
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className={classes.label}>Voyage</TableCell>
                <TableCell>{booking.tripName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Passager</TableCell>
                <TableCell>{booking.passengerName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Date de départ</TableCell>
                <TableCell>{formatDate(booking.departureDate)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Nombre de places</TableCell>
                <TableCell>{booking.seats}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Prix total</TableCell>
                <TableCell>{formatPrice(booking.totalPrice)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <Divider />

        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Informations d'annulation
          </Typography>
          
          <TextField
            className={classes.formControl}
            label="Raison de l'annulation"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            variant="outlined"
            multiline
            rows={3}
            required
            helperText="Veuillez indiquer la raison de l'annulation"
          />

          <FormControl className={classes.formControl} variant="outlined">
            <InputLabel>Méthode de remboursement</InputLabel>
            <Select
              value={refundMethod}
              onChange={(e) => setRefundMethod(e.target.value)}
              label="Méthode de remboursement"
            >
              <MenuItem value="original_method">Méthode de paiement originale</MenuItem>
              <MenuItem value="bank_transfer">Virement bancaire</MenuItem>
              <MenuItem value="voucher">Bon d'achat</MenuItem>
              <MenuItem value="no_refund">Aucun remboursement</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className={classes.buttons}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={loading}
          >
            Retour
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirmer l\'annulation'}
          </Button>
        </div>
      </Paper>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingCancelPage;
